// src/utils/portalQuoteReads.js
//
// x7portal-6e1a3d58 — X7 (security audit 2026-10-15 #29). The portal reads estimates ONLY through
// the getPortalQuotes callable (field-allowlisted on the server: functions/utils/portalQuotes.js);
// the Firestore rules no longer let a portal customer read an estimate or approval document. These
// helpers hand the pages the same shapes they used before — a snapshot-like object and plain
// records whose dates are real Firestore Timestamps again — so the page code barely changes.

import { httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { functions } from '../firebase/config';

// { __ts: millis } (how the server sends a Timestamp) → Timestamp, recursively.
export function reviveTimestamps(v) {
    if (Array.isArray(v)) return v.map(reviveTimestamps);
    if (v && typeof v === 'object') {
        if (Object.keys(v).length === 1 && typeof v.__ts === 'number') return Timestamp.fromMillis(v.__ts);
        const out = {};
        for (const [k, x] of Object.entries(v)) out[k] = reviveTimestamps(x);
        return out;
    }
    return v;
}

const call = (data) => httpsCallable(functions, 'getPortalQuotes')(data);

/** The customer's visible estimates (list rows), newest first. */
export async function listPortalQuotes() {
    const res = await call({});
    const rows = (res.data?.quotes || []).map(reviveTimestamps);
    const ms = (q) => (q.createdAt?.toMillis ? q.createdAt.toMillis() : 0);
    return rows.sort((a, b) => ms(b) - ms(a));
}

/**
 * One estimate + its latest approval receipt.
 * Returns { snap, latestApproval } where snap mimics a DocumentSnapshot (exists(), id, data()).
 * An estimate the customer may not see rejects with { code: 'permission-denied' } — exactly what
 * the old direct read did — so the page's "isn't available" panel still appears.
 */
export async function fetchPortalQuote(quoteId) {
    const res = await call({ quoteId });
    if (res.data?.unavailable) {
        const err = new Error('This estimate is not available.');
        err.code = 'permission-denied';
        err.reason = res.data.reason || null; // estdel-f9ac8ba8 — 'deleted' = the customer's own estimate, deleted
        throw err;
    }
    const quote = reviveTimestamps(res.data?.quote || null);
    const latestApproval = res.data?.latestApproval ? reviveTimestamps(res.data.latestApproval) : null;
    const { id, ...data } = quote || {};
    return {
        snap: { exists: () => !!quote, id: id || quoteId, data: () => data },
        latestApproval,
    };
}
